import React from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Cushion,
  Typography,
  GridItem,
  MaxWidth,
  Button,
  Space,
  FlexContainer,
  Show,
} from '@pitchero/react-ui';
import { Trans } from '../../../lib/i18n';
import Nl2Br from '../../nl2br';

import CLUB_PROP_TYPES from '../../../lib/prop-types/club';
import clubUrl from '../../../lib/routing/club-url';
import ClubLink from '../../club-link';
import NoBackground from '../../club-frame/header/no-background';
import AssignmentBadge from './product-page-assignment-badge';
import PlacehodlerProductPage from './placeholder-product-page';
import DonationAmount from './donation-amount';
import VariantSelector from './variant-selector';
import ProductList from '../product-list';
import FilterBar from '../filter-bar';
import FundraisingTotal from './fundraising-total';
import DonationsList from './donations-list';
import ProductImages from './images';

const ProductPage = ({
  club,
  isAddingToBasket,
  isLoading,
  product,
  addToBasket,
  isLoadingRelatedProducts,
  relatedProducts,
  isLoggedIn,
}) => {
  if (isLoading || !product) {
    return (
      <>
        <Show minWidth="tab" component="div">
          <FilterBar />
        </Show>
        <PlacehodlerProductPage />
      </>
    );
  }

  const {
    id,
    name,
    images,
    category,
    description,
    assignment,
    fundraisingCount,
    fundraisingPercent,
    fundraisingTarget,
    fundraisingTotal,
    groupDescription,
    recentDonations,
    showFundraisingTotal,
    userDefinedDonation,
  } = product;

  return (
    <>
      <Show minWidth="tab" component="div">
        <FilterBar selectedCategory={category} />
      </Show>
      <Cushion
        all="medium"
        vertical="xlarge"
        responsive={[
          { minWidth: 'tab', props: { all: 'xlarge' } },
          { minWidth: 'desk', props: { all: 'xxxlarge' } },
        ]}
        component="div"
      >
        <NoBackground />
        <MaxWidth component="div" center maxWidth={970}>
          <Grid
            className="product-page__main-grid"
            rowGap="medium"
            responsive={[
              {
                minWidth: 'tab',
                props: { columns: '300px 1fr', columnGap: 'large' },
              },
              {
                minWidth: 'lap',
                props: { columns: '400px 1fr' },
              },
              {
                minWidth: 'desk',
                props: { columnGap: 'xxxlarge' },
              },
            ]}
          >
            <ProductImages images={images} productName={name} />
            <GridItem className="product-page__details-grid">
              <AssignmentBadge assignment={assignment} />
              <Grid rowGap="small">
                <Typography preset="name" color="dustygrey">
                  <Trans i18nKey={`shop:${category}`}>{category}</Trans>
                </Typography>
                <Typography preset="title--large">{name}</Typography>
                <GridItem>
                  <Cushion vertical={groupDescription && 'small'} component="div">
                    <Typography preset="subtitle--small" color="black" component="div">
                      {groupDescription}
                    </Typography>
                  </Cushion>
                  <Cushion bottom={description && 'small'} component="div">
                    <Typography preset="body--small" color="dovegrey">
                      <Nl2Br>{description}</Nl2Br>
                    </Typography>
                  </Cushion>
                </GridItem>
                {showFundraisingTotal && (
                  <FundraisingTotal
                    fundraisingCount={fundraisingCount}
                    fundraisingPercent={fundraisingPercent}
                    fundraisingTarget={fundraisingTarget}
                    fundraisingTotal={fundraisingTotal}
                    shareProps={{
                      url: clubUrl(club, 'shop.product', { _name: name, id }),
                      title: name,
                      imageUrl: images[0].url,
                    }}
                  />
                )}
                {userDefinedDonation ? (
                  <DonationAmount
                    isAddingToBasket={isAddingToBasket}
                    product={product}
                    addToBasket={addToBasket}
                    isLoggedIn={isLoggedIn}
                  />
                ) : (
                  <VariantSelector
                    product={product}
                    addToBasket={addToBasket}
                    isLoggedIn={isLoggedIn}
                  />
                )}
                <DonationsList donations={recentDonations} />
              </Grid>
            </GridItem>
          </Grid>
        </MaxWidth>
        {(isLoadingRelatedProducts || relatedProducts.length > 0) && (
          <MaxWidth
            component="div"
            maxWidth={1366}
            center
            className="product-page__related_products"
          >
            <Cushion
              top="xlarge"
              bottom="medium"
              responsive={[
                { minWidth: 'tab', props: { vertical: 'xxlarge', horizontal: 'small' } },
              ]}
            >
              <Typography preset="title">
                <Trans i18nKey="shop:related_products">Related Products</Trans>
              </Typography>
              <Space
                vertical="medium"
                component="div"
                responsive={[{ minWidth: 'tab', props: { vertical: 'large' } }]}
              >
                <ProductList isLoading={isLoadingRelatedProducts} products={relatedProducts} />
              </Space>
              <FlexContainer justifyContent="center">
                <ClubLink route="shop.category" params={{ category }}>
                  <a>
                    <Button theme="textWithBorder">
                      <Trans i18nKey="common:view_all">View All</Trans>
                    </Button>
                  </a>
                </ClubLink>
              </FlexContainer>
            </Cushion>
          </MaxWidth>
        )}
      </Cushion>
    </>
  );
};

ProductPage.defaultProps = {
  product: null,
};

ProductPage.propTypes = {
  addToBasket: PropTypes.func.isRequired,
  club: CLUB_PROP_TYPES.isRequired,
  isAddingToBasket: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingRelatedProducts: PropTypes.bool.isRequired,
  relatedProducts: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  product: PropTypes.shape({
    id: PropTypes.number,
    category: PropTypes.string,
    description: PropTypes.string,
    groupDescription: PropTypes.string,
    name: PropTypes.string,
    assignment: PropTypes.shape(),
    requiresAssignment: PropTypes.bool, // Does the user need to have been assigned to the product to purchase.
    images: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        url: PropTypes.string,
      }),
    ),
    fundraisingCount: PropTypes.number,
    fundraisingPercent: PropTypes.number,
    fundraisingTarget: PropTypes.string,
    fundraisingTotal: PropTypes.string,
    recentDonations: PropTypes.arrayOf(PropTypes.shape()),
    showFundraisingTotal: PropTypes.bool,
    userDefinedDonation: PropTypes.bool,
    outOfStock: PropTypes.bool,
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
        paymentType: PropTypes.string,
        setupFee: PropTypes.string,
        amountDescription: PropTypes.string,
        amount: PropTypes.string,
        currencyCode: PropTypes.string,
        totalPayable: PropTypes.string,
      }),
    ),
  }),
};

export default ProductPage;
